/**
 * The checks the prompt screen runs.
 *
 * The チェック panel beside an instruction - 承認済み情報にないことを書かない指示が
 * 見つかりません, and the rest - used to be regular expressions compiled into the
 * server. The people who know when a clause stops being enough are not the
 * people who can edit TypeScript, so every change was a deploy. Migration 095
 * made them rows; this is where they are read and written.
 *
 * WHAT A ROW MEANS, in the three kinds:
 *
 *   必須  the instruction must CONTAIN this. Missing is a 確認 that blocks 有効化,
 *         not the save - a draft may be half-written, a release may not be
 *         missing its 医療広告 clause.
 *   禁止  the instruction must NOT contain this. Present refuses the save, and
 *         only these do: an instruction that tells the model to invent is the
 *         one thing no amount of review downstream can undo.
 *   画像   a prohibition a picture prompt's appended block may not drop, checked
 *         only when somebody has rewritten that block (migration 094).
 *
 * EMPTYING THIS LIST DOES NOT SWITCH CHECKING OFF. With no rows the server runs
 * the list compiled into the build, which is shown below each kind as 既定. That
 * is deliberate and it is said on the screen: a product whose checks can be
 * disabled by deleting rows is one nobody can rely on.
 */
import { useState } from 'react';
import { Link, useLoaderData, useRevalidator } from 'react-router';
import { ApiError, del, patch, post } from '../api.ts';
import { Card, Empty, Pill, Table, jaDate } from '../components/ui.tsx';
import { DangerButton } from '../components/DangerButton.tsx';

export interface PromptCheckRow {
  id: string;
  code: string;
  kind: 'REQUIRED' | 'BANNED' | 'APPENDED';
  label: string;
  pattern: string;
  flags: string;
  task_types: string[];
  enabled: boolean;
  note: string;
  sort_order: number;
  updated_at: string;
  updated_by_name: string | null;
}

export interface PromptChecksData {
  items: PromptCheckRow[];
  /** True when no row exists at all - which means nothing is checked. */
  empty: boolean;
  taskTypes: string[];
}

const KIND_LABEL: Record<PromptCheckRow['kind'], string> = {
  REQUIRED: '必須（不足なら有効化できない）',
  BANNED: '禁止（含まれていたら保存できない）',
  APPENDED: '画像の禁止事項（書き換えたときだけ確認）',
};

const KIND_HINT: Record<PromptCheckRow['kind'], string> = {
  REQUIRED: '指示文にこの表現が見つからないと、確認として表示されます。',
  BANNED: '指示文にこの表現があると、保存そのものを拒否します。否定形（〜しません）は違反とみなしません。',
  APPENDED: '画像プロンプトの「常に末尾に追加される内容」を書き換えたとき、この表現が残っているかを確認します。',
};

const TASK_LABEL: Record<string, string> = {
  outline: '構成案',
  body: '記事全体',
  section: '節ごと',
  proposals: 'タイトル案',
  rewrite: '部分リライト',
  persona: 'ペルソナ',
  image: '人物写真',
  article_image: '記事の画像',
  facts: 'サイト読み取り',
  profile: 'クリニック情報読み取り',
};

const BLANK = {
  code: '',
  kind: 'REQUIRED' as PromptCheckRow['kind'],
  label: '',
  pattern: '',
  flags: '',
  taskTypes: [] as string[],
  note: '',
};

export default function AdminPromptChecks() {
  const loaded = useLoaderData<{
    prompts: PromptChecksData;
    editorial: { items: EditorialRow[] };
  }>();
  const data = loaded.prompts;
  const revalidator = useRevalidator();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(BLANK);

  const report = (err: unknown, fallback: string) =>
    setError(err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : fallback);

  async function run(id: string, work: () => Promise<unknown>) {
    setBusy(id);
    setError(null);
    try {
      await work();
      await revalidator.revalidate();
    } catch (err) {
      report(err, '変更できませんでした');
    } finally {
      setBusy(null);
    }
  }

  const kinds: PromptCheckRow['kind'][] = ['REQUIRED', 'BANNED', 'APPENDED'];

  return (
    <>
    <Card
      title="プロンプトのチェック項目"
      caption="プロンプト編集画面に出る「チェック」の内容です。ここを変えると、次の入力から反映されます。"
      actions={
        <>
          <Link className="btn btn--sm btn--ghost" to="/admin/prompts">
            プロンプト一覧に戻る
          </Link>
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => {
              setAdding((open) => !open);
              setDraft(BLANK);
            }}
          >
            {adding ? '閉じる' : '新しいチェック'}
          </button>
        </>
      }
    >
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {adding && (
        <div className="field" style={{ marginBottom: 16 }}>
          <label htmlFor="c-code">識別子（英小文字・数字・_）</label>
          <input
            id="c-code"
            value={draft.code}
            placeholder="例）no_price_claim"
            onChange={(event) => setDraft({ ...draft, code: event.target.value })}
          />
          <label htmlFor="c-kind">種類</label>
          <select
            id="c-kind"
            className="input"
            value={draft.kind}
            onChange={(event) =>
              setDraft({ ...draft, kind: event.target.value as PromptCheckRow['kind'] })
            }
          >
            {kinds.map((kind) => (
              <option key={kind} value={kind}>
                {KIND_LABEL[kind]}
              </option>
            ))}
          </select>
          <span className="field__hint">{KIND_HINT[draft.kind]}</span>
          <label htmlFor="c-label">画面に出る文言</label>
          <input
            id="c-label"
            value={draft.label}
            placeholder="例）料金の断定を禁じる指示"
            onChange={(event) => setDraft({ ...draft, label: event.target.value })}
          />
          <label htmlFor="c-pattern">探す表現（正規表現）</label>
          <input
            id="c-pattern"
            value={draft.pattern}
            placeholder="例）最安|業界最安値"
            onChange={(event) => setDraft({ ...draft, pattern: event.target.value })}
          />
          <span className="field__hint">
            「|」で言い換えを並べます。解釈できない書き方は保存時に拒否されます。
          </span>
          <label>対象の用途（選ばなければすべて）</label>
          <div className="chips">
            {data.taskTypes.map((task) => (
              <button
                key={task}
                type="button"
                className="chip"
                aria-pressed={draft.taskTypes.includes(task)}
                onClick={() =>
                  setDraft({
                    ...draft,
                    taskTypes: draft.taskTypes.includes(task)
                      ? draft.taskTypes.filter((one) => one !== task)
                      : [...draft.taskTypes, task],
                  })
                }
              >
                {TASK_LABEL[task] ?? task}
              </button>
            ))}
          </div>
          <label htmlFor="c-note">このチェックがある理由</label>
          <textarea
            id="c-note"
            rows={2}
            value={draft.note}
            onChange={(event) => setDraft({ ...draft, note: event.target.value })}
          />
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn btn--sm"
              disabled={busy !== null || draft.code === '' || draft.pattern === ''}
              onClick={() =>
                void run('new', async () => {
                  await post('/admin/prompt-checks', {
                    code: draft.code,
                    kind: draft.kind,
                    label: draft.label,
                    pattern: draft.pattern,
                    flags: draft.flags,
                    taskTypes: draft.taskTypes,
                    note: draft.note,
                  });
                  setAdding(false);
                  setDraft(BLANK);
                })
              }
            >
              追加する
            </button>
          </div>
        </div>
      )}

      {kinds.map((kind) => {
        const rows = data.items.filter((item) => item.kind === kind);
        return (
          <section key={kind} style={{ marginBottom: 24 }}>
            <h3>{KIND_LABEL[kind]}</h3>
            <p className="muted small">{KIND_HINT[kind]}</p>
            {rows.length === 0 ? (
              <Empty>この種類のチェックは登録されていません。有効化の際にこの項目は確認されません。</Empty>
            ) : (
              <Table
                head={
                  <>
                    <th scope="col">文言</th>
                    <th scope="col">探す表現</th>
                    <th scope="col">対象</th>
                    <th scope="col">状態</th>
                    <th scope="col">最終更新</th>
                    <th scope="col" />
                  </>
                }
              >
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <EditableText
                        value={row.label}
                        disabled={busy !== null}
                        onSave={(label) => {
                          void run(row.id, () => patch(`/admin/prompt-checks/${row.id}`, { label }));
                        }}
                      />
                      <span className="muted small" style={{ display: 'block' }}>
                        {row.code}
                        {row.note ? `・${row.note}` : ''}
                      </span>
                    </td>
                    <td>
                      <EditableText
                        mono
                        value={row.pattern}
                        disabled={busy !== null}
                        onSave={(pattern) => {
                          void run(row.id, () =>
                            patch(`/admin/prompt-checks/${row.id}`, { pattern }),
                          );
                        }}
                      />
                    </td>
                    <td className="small">
                      {row.task_types.length === 0
                        ? 'すべて'
                        : row.task_types.map((task) => TASK_LABEL[task] ?? task).join('、')}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost"
                        disabled={busy !== null}
                        onClick={() =>
                          void run(row.id, () =>
                            patch(`/admin/prompt-checks/${row.id}`, { enabled: !row.enabled }),
                          )
                        }
                      >
                        {row.enabled ? <Pill tone="ok">有効</Pill> : <Pill tone="muted">停止中</Pill>}
                      </button>
                    </td>
                    <td className="small">
                      {jaDate(row.updated_at)}
                      {row.updated_by_name ? `・${row.updated_by_name}` : ''}
                    </td>
                    <td>
                      <DangerButton
                        label="削除"
                        confirmText={row.code}
                        consequences={[
                          'このチェックは次の入力から実行されなくなります。',
                          'この種類の登録がすべて無くなった場合は、ビルド同梱の既定が動きます。',
                        ]}
                        onConfirm={() => del(`/admin/prompt-checks/${row.id}`)}
                        onDone={() => void revalidator.revalidate()}
                      />
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </section>
        );
      })}

      {data.empty && (
        <p className="alert" role="alert">
          チェックが1件も登録されていません。プロンプトの保存・有効化で何も確認されない状態です。
        </p>
      )}
    </Card>

    <EditorialChecksCard items={loaded.editorial.items} onChanged={() => void revalidator.revalidate()} />
    </>
  );
}

/** A value that becomes an input when you click it, and saves on blur. */
function EditableText({
  value,
  onSave,
  disabled,
  mono,
}: {
  value: string;
  onSave: (next: string) => void;
  disabled: boolean;
  mono?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <button
        type="button"
        className="btn btn--sm btn--ghost"
        disabled={disabled}
        style={mono ? { fontFamily: 'monospace' } : undefined}
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
      >
        {value}
      </button>
    );
  }
  return (
    <input
      autoFocus
      value={draft}
      style={mono ? { fontFamily: 'monospace' } : undefined}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        setEditing(false);
        if (draft !== value) onSave(draft);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') setEditing(false);
      }}
    />
  );
}

export interface EditorialRow {
  id: string;
  code: string;
  kind: 'TELL' | 'COMPARABLE_HEADING';
  label: string;
  pattern: string;
  flags: string;
  enabled: boolean;
  note: string;
  sort_order: number;
  updated_at: string;
  updated_by_name: string | null;
}

const EDITORIAL_KIND_LABEL: Record<EditorialRow['kind'], string> = {
  TELL: 'AIらしい言い回し',
  COMPARABLE_HEADING: '比較を示す見出しの言葉',
};

const EDITORIAL_KIND_HINT: Record<EditorialRow['kind'], string> = {
  TELL: '記事本文にこの表現が見つかると、文章チェックに「AIらしい言い回しが残っています」として表示されます。',
  COMPARABLE_HEADING:
    '見出しにこの言葉が含まれ、その節に表（|区切り）が無いと、「比較の節に表がありません」として表示されます。',
};

const EDITORIAL_BLANK = {
  code: '',
  kind: 'TELL' as EditorialRow['kind'],
  label: '',
  pattern: '',
  flags: '',
  note: '',
};

/**
 * The 文章チェック panel's own patterns (記事編集画面の「AIらしい言い回し」「比較の節に
 * 表がありません」の元になっている表現一覧). Migration 096.
 *
 * Same screen as the prompt checks above rather than a separate page: both are
 * "what makes something get flagged, editable instead of hidden in code", and
 * a second sidebar entry for a second kind of the same thing is exactly what
 * this screen was pulled out of the sidebar to avoid.
 */
function EditorialChecksCard({
  items,
  onChanged,
}: {
  items: EditorialRow[];
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EDITORIAL_BLANK);

  const report = (err: unknown, fallback: string) =>
    setError(err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : fallback);

  async function run(id: string, work: () => Promise<unknown>) {
    setBusy(id);
    setError(null);
    try {
      await work();
      onChanged();
    } catch (err) {
      report(err, '変更できませんでした');
    } finally {
      setBusy(null);
    }
  }

  const kinds: EditorialRow['kind'][] = ['TELL', 'COMPARABLE_HEADING'];

  return (
    <Card
      title="文章チェックの言い回し"
      caption="記事編集画面の「文章チェック」で使う表現の一覧です。AIは使わず、ここに登録した表現だけを見ます。"
      actions={
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          onClick={() => {
            setAdding((open) => !open);
            setDraft(EDITORIAL_BLANK);
          }}
        >
          {adding ? '閉じる' : '新しい表現'}
        </button>
      }
    >
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {adding && (
        <div className="field" style={{ marginBottom: 16 }}>
          <label htmlFor="e-code">識別子（英小文字・数字・_）</label>
          <input
            id="e-code"
            value={draft.code}
            placeholder="例）unnatural_closing"
            onChange={(event) => setDraft({ ...draft, code: event.target.value })}
          />
          <label htmlFor="e-kind">種類</label>
          <select
            id="e-kind"
            className="input"
            value={draft.kind}
            onChange={(event) =>
              setDraft({ ...draft, kind: event.target.value as EditorialRow['kind'] })
            }
          >
            {kinds.map((kind) => (
              <option key={kind} value={kind}>
                {EDITORIAL_KIND_LABEL[kind]}
              </option>
            ))}
          </select>
          <span className="field__hint">{EDITORIAL_KIND_HINT[draft.kind]}</span>
          <label htmlFor="e-label">画面に出る文言</label>
          <input
            id="e-label"
            value={draft.label}
            placeholder="例）「ご確認ください」"
            onChange={(event) => setDraft({ ...draft, label: event.target.value })}
          />
          <label htmlFor="e-pattern">探す表現（正規表現）</label>
          <input
            id="e-pattern"
            value={draft.pattern}
            placeholder="例）ご確認ください"
            onChange={(event) => setDraft({ ...draft, pattern: event.target.value })}
          />
          <span className="field__hint">
            「|」で言い換えを並べます。解釈できない書き方は保存時に拒否されます。TELL は本文全体、比較の見出しは見出しの文字列だけを見ます。
          </span>
          <label htmlFor="e-note">このチェックがある理由</label>
          <textarea
            id="e-note"
            rows={2}
            value={draft.note}
            onChange={(event) => setDraft({ ...draft, note: event.target.value })}
          />
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn btn--sm"
              disabled={busy !== null || draft.code === '' || draft.pattern === ''}
              onClick={() =>
                void run('new', async () => {
                  await post('/admin/editorial-checks', {
                    code: draft.code,
                    kind: draft.kind,
                    label: draft.label,
                    pattern: draft.pattern,
                    flags: draft.flags,
                    note: draft.note,
                  });
                  setAdding(false);
                  setDraft(EDITORIAL_BLANK);
                })
              }
            >
              追加する
            </button>
          </div>
        </div>
      )}

      {kinds.map((kind) => {
        const rows = items.filter((item) => item.kind === kind);
        return (
          <section key={kind} style={{ marginBottom: 24 }}>
            <h3>{EDITORIAL_KIND_LABEL[kind]}</h3>
            <p className="muted small">{EDITORIAL_KIND_HINT[kind]}</p>
            {rows.length === 0 ? (
              <Empty>この種類の登録はありません。記事編集画面では何も表示されません。</Empty>
            ) : (
              <Table
                head={
                  <>
                    <th scope="col">文言</th>
                    <th scope="col">探す表現</th>
                    <th scope="col">状態</th>
                    <th scope="col">最終更新</th>
                    <th scope="col" />
                  </>
                }
              >
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <EditableText
                        value={row.label}
                        disabled={busy !== null}
                        onSave={(label) => {
                          void run(row.id, () =>
                            patch(`/admin/editorial-checks/${row.id}`, { label }),
                          );
                        }}
                      />
                      <span className="muted small" style={{ display: 'block' }}>
                        {row.code}
                        {row.note ? `・${row.note}` : ''}
                      </span>
                    </td>
                    <td>
                      <EditableText
                        mono
                        value={row.pattern}
                        disabled={busy !== null}
                        onSave={(pattern) => {
                          void run(row.id, () =>
                            patch(`/admin/editorial-checks/${row.id}`, { pattern }),
                          );
                        }}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost"
                        disabled={busy !== null}
                        onClick={() =>
                          void run(row.id, () =>
                            patch(`/admin/editorial-checks/${row.id}`, { enabled: !row.enabled }),
                          )
                        }
                      >
                        {row.enabled ? <Pill tone="ok">有効</Pill> : <Pill tone="muted">停止中</Pill>}
                      </button>
                    </td>
                    <td className="small">
                      {jaDate(row.updated_at)}
                      {row.updated_by_name ? `・${row.updated_by_name}` : ''}
                    </td>
                    <td>
                      <DangerButton
                        label="削除"
                        confirmText={row.code}
                        consequences={[
                          '記事編集画面の文章チェックで、次の入力からこの表現が表示されなくなります。',
                        ]}
                        onConfirm={() => del(`/admin/editorial-checks/${row.id}`)}
                        onDone={onChanged}
                      />
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </section>
        );
      })}

      {items.length === 0 && (
        <p className="alert" role="alert">
          登録が1件もありません。記事編集画面の文章チェックには、AIらしい言い回しや比較の表チェックが一切表示されません。
        </p>
      )}
    </Card>
  );
}
